import { HttpBackend, HttpClient } from '@angular/common/http';
import {
  DOCUMENT,
  EnvironmentProviders,
  inject,
  makeEnvironmentProviders,
  NgZone,
  provideEnvironmentInitializer,
  Provider,
} from '@angular/core';
import {
  AuthConfig,
  DateTimeProvider,
  DefaultHashHandler,
  HashHandler,
  NullValidationHandler,
  OAuthLogger,
  OAuthNoopResourceServerErrorHandler,
  OAuthResourceServerErrorHandler,
  OAuthService,
  OAuthStorage,
  SystemDateTimeProvider,
  UrlHelperService,
  ValidationHandler,
} from 'angular-oauth2-oidc';

import { IOAuthService } from './oauth/ioauth-service';
import { MockOAuthService } from './oauth/mock.oauth-service';
import { createDefaultLogger, createDefaultStorage, PLUGIN_URL_MARKERS } from './oauth/oauth-factories';
import { HypecastCredentialsAuthenticationGuard } from './guards/hypecast-credentials-auth.guard';
import { HypecastAuthenticationService } from './services/authentication.service';
import { HypecastOAuthService } from './services/hypecast-oauth.service';
import { IHypecastOAuthService } from './services/ihypecast-oauth.service';
import { MockHypecastOAuthService } from './services/mock.hypecast-oauth.service';

/**
 * Builds the npm OAuthService with a private HttpClient over HttpBackend.
 * Discovery and token requests must bypass the application interceptor chain;
 * otherwise the OAuth bearer interceptor would resolve IOAuthService while
 * IOAuthService itself is still being constructed.
 */
export function createOAuthService(
  ngZone: NgZone,
  httpBackend: HttpBackend,
  storage: OAuthStorage,
  validationHandler: ValidationHandler,
  urlHelper: UrlHelperService,
  logger: OAuthLogger,
  hashHandler: HashHandler,
  document: Document,
  dateTimeProvider: DateTimeProvider
): OAuthService {
  return new OAuthService(
    ngZone,
    new HttpClient(httpBackend),
    storage,
    validationHandler,
    new AuthConfig(),
    urlHelper,
    logger,
    hashHandler,
    document,
    dateTimeProvider
  );
}

export function provideHypecastAuthentication(
  validationHandlerClass: any = NullValidationHandler
): EnvironmentProviders {
  const isPluginContext = PLUGIN_URL_MARKERS.some((marker) => window.location.href.includes(marker));

  const providers: Provider[] = [
    isPluginContext
      ? { provide: IOAuthService, useClass: MockOAuthService }
      : {
          provide: IOAuthService,
          useFactory: createOAuthService,
          deps: [NgZone, HttpBackend, OAuthStorage, ValidationHandler, UrlHelperService, OAuthLogger, HashHandler, DOCUMENT, DateTimeProvider],
        },
    isPluginContext
      ? { provide: IHypecastOAuthService, useClass: MockHypecastOAuthService }
      : { provide: IHypecastOAuthService, useClass: HypecastOAuthService },
    UrlHelperService,
    HypecastCredentialsAuthenticationGuard,
    HypecastAuthenticationService,
    { provide: OAuthLogger, useFactory: createDefaultLogger },
    { provide: OAuthStorage, useFactory: createDefaultStorage },
    { provide: ValidationHandler, useClass: validationHandlerClass },
    { provide: HashHandler, useClass: DefaultHashHandler },
    { provide: DateTimeProvider, useClass: SystemDateTimeProvider },
    { provide: OAuthResourceServerErrorHandler, useClass: OAuthNoopResourceServerErrorHandler },
  ];

  return makeEnvironmentProviders([
    providers,
    provideEnvironmentInitializer(() => {
      inject(IHypecastOAuthService);
    }),
  ]);
}
